import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  Autocomplete,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Colors } from "../../../../Styles/Colors";
import ShowComponents from "../Common/ShowComponent";
import { renderIconButton, renderTitle } from "../Common/DocCommonComponent";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import {
  CommonDeleteButton, CommonEditButton, DeletePopup,
} from "../../../../Components/Common Components/CommonComponents";
import { ImagePaths } from "../../../../Utility/ImagePaths";
import RestAPIService from "../../../../Utility/Services/RestAPIService";

const headers = [
  "Code",
  "Vyaadhi",
  "Classification",
  "Dosha",
  "Dooshya",
  "Srothas",
  "Saama",
  "Description",
  "Action",
];

const DocAyurvedicDiagnosis = ({ Title, ...props }) => {
  const [state, setState] = useState({
    // input
    codeValue: null,
    vyaadhiValue: null,
    classificationValue: null,
    doshaValue: null,
    dooshyaValue: null,
    srotasValue: null,
    saama: false,
    description: "",

    // master + data
    diagnosisList: [],
    diagnosisData: [],
    filteredCodeList: [],

    // UI
    isEdit: false,
    selectedId: null,
    postLoading: false,
    dltLineClk: false,
    deleteLineItem: null,
    removeLineData: false,
  });

  const updateState = (u) => setState((p) => ({ ...p, ...u }));

  // ====================== API LOAD ======================
  const getApiCall = useCallback(() => {
    getDiagnosisData();
    getDiagnosisMaster();
  }, [props.appointmentId, props.isFromIp, props.ipAdmissionId]);


  useEffect(() => {
    props.registerRefreshFunction("ayurvedic_diagnosis", getDiagnosisData);
  }, []);

  const getDiagnosisMaster = async () => {
    try {
      const res = await RestAPIService.getAll(Serviceurls.AYURVEDA_DIAGNOSIS_MASTER);
      if (res?.data?.status === "success") {
        const list = res.data.data?.filter(i => i.is_active) || [];
        updateState({ diagnosisList: list });
      } else props.errorMessage(res?.data?.message);
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  const getDiagnosisData = async () => {
    try {
      const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
      const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
      const res = await RestAPIService.getAll(
        `${Serviceurls.AYURVEDA_DOCTOR_DIAGNOSIS}${params}`
      );
      if (res?.data?.status === "success") {
        updateState({ diagnosisData: res.data.data || [] });
      } else props.errorMessage(res?.data?.message);
    } catch (e) {
      props.errorMessage(e.message);
    }
  };

  // ====================== HANDLERS ======================
  const onClear = () =>
    updateState({
      codeValue: null,
      vyaadhiValue: null,
      classificationValue: null,
      doshaValue: null,
      dooshyaValue: null,
      srotasValue: null,
      saama: false,
      description: "",
      isEdit: false,
      selectedId: null,
    });

  const postDiagnosis = async () => {
    try {
      const { appointmentId, ipAdmissionId, clinicId, doctorId, isFromIp } = props;
      const data = {
        id: state.selectedId ? state.selectedId : null,
        diagnosis_master: state?.codeValue ? state?.codeValue?.id : null,
        code: state.codeValue?.code || "",
        vyaadhi_name: state.vyaadhiValue?.vyaadhi_name || "",
        classification: state.classificationValue?.classification || "",
        dosha: state.doshaValue?.dosha || "",
        dooshya: state.dooshyaValue?.dooshya || "",
        srotas: state.srotasValue?.srotas || "",
        saama: state.saama,
        description: state.description || "",
      };
      if (isFromIp) {
        data.ip_admission_id = ipAdmissionId;
        data.clinic_id = clinicId;
        data.doctor_id = doctorId;
      } else {
        data.appointment_id = appointmentId;
      }

      const params = Serviceurls.AYURVEDA_DOCTOR_DIAGNOSIS;
      updateState({ postLoading: true });

      const apiCall = state.selectedId
        ? RestAPIService.update(state.selectedId, data, params)
        : RestAPIService.create(data, params);

      const res = await apiCall;
      if (res?.data?.status === "success") {
        props.successMessage(res.data.message || "Saved successfully");
        onClear();
        getDiagnosisData();
      } else props.errorMessage(res?.data?.message || "Save failed");

      updateState({ postLoading: false });
    } catch (e) {
      updateState({ postLoading: false });
      props.errorMessage(e.message);
    }
  };

  const onEdit = (item) => {
    updateState({
      isEdit: true,
      selectedId: item.id,
      codeValue: item,
      vyaadhiValue: item,
      classificationValue: item,
      doshaValue: item,
      dooshyaValue: item,
      srotasValue: item,
      saama: !!item.saama,
      description: item.description || "",
    });
  };

  const deleteLinePop = () =>
    updateState({ dltLineClk: false, deleteLineItem: null, removeLineData: false });

  const deleteLineItem = async () => {
    try {
      updateState({ removeLineData: true });
      await RestAPIService.delete(
        `${Serviceurls.AYURVEDA_DOCTOR_DIAGNOSIS}${state.deleteLineItem?.id}/`
      );
      props.successMessage("Deleted successfully");
      deleteLinePop();
      getDiagnosisData();
    } catch (e) {
      updateState({ removeLineData: false });
      props.errorMessage(e.message);
    }
  };

  const disableBtn = useMemo(() => {
    return !(state.vyaadhiValue && state.codeValue) || props.isLocked;
  }, [state.vyaadhiValue, state.codeValue, props.isLocked]);

  // ====================== RENDER HELPERS ======================
  const renderAutoComplete = (label, stateKey, list, showKey, disabled = false) => {
    const value = state[stateKey] || null;
    return (
      <Autocomplete
        size="small"
        
        popupIcon={null}
        options={list || []}
        disable={state.isEdit}
        getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt[showKey] || "")}
        value={value}
        disabled={disabled}
        onChange={(e, val) => {
          updateState({ [stateKey]: val });
          if (stateKey === "codeValue" && val) {
            // auto-fill rest fields when selecting code
            updateState({
              vyaadhiValue: val,
              classificationValue: val,
              doshaValue: val,
              dooshyaValue: val,
              srotasValue: val,
              saama: !!val.saama,
              description: val.description || "",
            });
          }
          else if (stateKey === "vyaadhiValue" && val) {
            // filter diagnosis list by selected vyaadhi
            const filtered = state.diagnosisList.filter(
              (item) => item.vyaadhi_name === val.vyaadhi_name
            );
            if (filtered.length === 1) {
              const single = filtered[0];
              updateState({
                codeValue: single,
                vyaadhiValue: single,
                classificationValue: single,
                doshaValue: single,
                dooshyaValue: single,
                srotasValue: single,
                saama: !!single.saama,
                description: single.description || "",
              });
            } else {
              // clear code when multiple available, let user choose
              updateState({ codeValue: null, filteredCodeList: filtered });
            }
          }
          else if (stateKey === "codeValue" && !val) {
            updateState({
              vyaadhiValue: "",
              classificationValue: "",
              doshaValue: "",
              dooshyaValue: "",
              srotasValue: "",
              filteredCodeList: []
            })
          }
        }}
        renderInput={(params) => <TextField {...params} label={label} fullWidth size="small" />}
        sx={{ width: "100%" }}
      />
    );
  };

  const renderTextField = (label, stateKey, maxLength = 200) => (
    <TextField
      size="small"
      autoComplete="off"
      label={label}
      value={state[stateKey] || ""}
      inputProps={{ maxLength }}
      onChange={(e) => updateState({ [stateKey]: e.target.value })}
      sx={{ width: "100%" }}
    />
  );

  // ====================== UI ======================
  return (
    <ShowComponents onIntersection={getApiCall}>
      <Card
        sx={{
          width: "100%",
          height: "fit-content",
          padding: "0.3vw",
          marginBottom: "0.5vw",
          border: `2px solid ${props.isSelected ? Colors.themeDark : Colors.white}`,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding="0.5vw"
          borderBottom={`1px solid #E3E2E2`}
        >
          {renderTitle(Title, props.isSelected)}
        </Box>

        <Table size="small" stickyHeader aria-label="ayurvedic diagnosis">
          <TableHead>
            <TableRow>
              {headers.map((h, i) => (
                <TableCell
                  key={i}
                  align={i === headers.length - 1 ? "center" : "left"}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9vw",
                    color: Colors.SecondaryText,
                    backgroundColor: Colors.Background,
                    px: "0.3vw",
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Input Row */}
            <TableRow sx={{ "& td": { px: "0.2vw", verticalAlign: "middle" } }}>
              <TableCell sx={{ width: "8vw" }}>
                {renderAutoComplete("Code *", "codeValue", state.filteredCodeList?.length ? state.filteredCodeList : state.diagnosisList, "code")}
              </TableCell>
              <TableCell sx={{ width: "10vw" }}>
                {renderAutoComplete("Vyaadhi *", "vyaadhiValue", state.diagnosisList, "vyaadhi_name")}
              </TableCell>
              <TableCell sx={{ width: "10vw" }}>
                {renderAutoComplete("Classification", "classificationValue", state.diagnosisList, "classification", true)}
              </TableCell>
              <TableCell sx={{ width: "8vw" }}>
                {renderAutoComplete("Dosha", "doshaValue", state.diagnosisList, "dosha", true)}
              </TableCell>
              <TableCell sx={{ width: "8vw" }}>
                {renderAutoComplete("Dooshya", "dooshyaValue", state.diagnosisList, "dooshya", true)}
              </TableCell>
              <TableCell sx={{ width: "8vw" }}>
                {renderAutoComplete("Srothas", "srotasValue", state.diagnosisList, "srotas", true)}
              </TableCell>
              <TableCell sx={{ width: "3vw", textAlign: "end" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!state.saama}
                      onChange={(e) => updateState({ saama: e.target.checked })}
                      size="small"
                      readOnly
                    />
                  }
                  label=""
                />
              </TableCell>
              <TableCell sx={{ width: "10vw" }}>
                {renderTextField("Description", "description")}
              </TableCell>
              <TableCell align="center" sx={{ width: "1vw" }}>
                {renderIconButton(
                  state.isEdit ? "Update" : "Add",
                  disableBtn
                    ? ImagePaths.DisabledAddIcon.default
                    : ImagePaths.AddIcon.default,
                  postDiagnosis,
                  disableBtn,
                  "2vw"
                )}
              </TableCell>
            </TableRow>

            {/* Data Rows */}
            {state.diagnosisData?.length > 0 ? (
              state.diagnosisData.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.code || "-"}</TableCell>
                  <TableCell>{item.vyaadhi_name || "-"}</TableCell>
                  <TableCell>{item.classification || "-"}</TableCell>
                  <TableCell>{item.dosha || "-"}</TableCell>
                  <TableCell>{item.dooshya || "-"}</TableCell>
                  <TableCell>{item.srotas || "-"}</TableCell>
                  <TableCell align="center">
                    <Checkbox checked={!!item.saama} disabled size="small" />
                  </TableCell>
                  <TableCell>
                    {item.description ? (
                      <Tooltip title={item.description} placement="top">
                        <div>
                          {item.description.length > 25
                            ? item.description.slice(0, 22) + "..."
                            : item.description}
                        </div>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex">
                      <CommonEditButton
                        disable={state.dltLineClk || props.isLocked}
                        size={"1.5vw"}
                        onClick={() => onEdit(item)}
                      />
                      <CommonDeleteButton
                        disable={state.isEdit || state.dltLineClk || props.isLocked}
                        size={"1.5vw"}
                        onClick={() =>
                          updateState({ dltLineClk: true, deleteLineItem: item })
                        }
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  No Records To Be Shown
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {state.dltLineClk && (
          <DeletePopup
            DeletTitle="Are you sure you want to delete?"
            deleteAlertPopupClose={deleteLinePop}
            removeData={deleteLineItem}
            disable={state.removeLineData}
          />
        )}
      </Card>
    </ShowComponents>
  );
};

export default DocAyurvedicDiagnosis;
